import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Flame } from 'lucide-react';
import VehicleCard from './VehicleCard';
import { VehicleAPI } from '../mock';

const TABS = ['All', 'SUV', 'Sedan', 'Hatchback'];

export default function FeaturedVehicles() {
  const [tab, setTab] = useState('All');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Fetch both featured cars AND any on-sale cars so On Sale always gets spotlight
    Promise.all([
      VehicleAPI.list({ featured: true, limit: 12 }),
      VehicleAPI.list({ status: 'on_sale', limit: 12 }),
    ])
      .then(([feat, sale]) => {
        const seen = new Set();
        const combined = [];
        for (const v of [...sale, ...feat]) {
          if (!seen.has(v.id)) { seen.add(v.id); combined.push(v); }
        }
        // Sort: on_sale first → featured → newest
        combined.sort((a, b) => {
          const aSale = a.status === 'on_sale' ? 0 : 1;
          const bSale = b.status === 'on_sale' ? 0 : 1;
          if (aSale !== bSale) return aSale - bSale;
          const aFeat = a.featured ? 0 : 1;
          const bFeat = b.featured ? 0 : 1;
          if (aFeat !== bFeat) return aFeat - bFeat;
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
        setItems(combined.slice(0, 9));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const list = tab === 'All' ? items : items.filter(v => v.body === tab);

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-black text-white px-3.5 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold">
              <Flame size={12} className="text-[#e11d2c]" /> Featured Vehicles
            </div>
            <h2 className="font-display mt-5 text-[36px] sm:text-[48px] lg:text-[56px] font-black leading-[1.02] tracking-tight text-black">
              Curated cars, <span className="text-[#e11d2c]">priced right</span>.
            </h2>
            <p className="mt-4 text-black/60 text-[15px] max-w-lg">
              Our handpicked highlights this week — verified history, freshly inspected, and ready to drive.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {TABS.map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`h-10 px-5 rounded-full text-[13px] font-semibold tracking-wide transition-colors border ${
                  tab === t ? 'bg-[#e11d2c] text-white border-[#e11d2c]' : 'bg-white text-black border-black/15 hover:border-black'
                }`}
              >{t}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="aspect-[4/3] bg-black/5 rounded-2xl animate-pulse" />)}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-12 text-black/55">No featured vehicles yet — add some from the admin panel.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map(v => <VehicleCard key={v.id} v={v} />)}
          </div>
        )}

        <div className="mt-12 flex justify-center">
          <Link to="/inventory" className="inline-flex items-center gap-2 text-black font-semibold border-b-2 border-[#e11d2c] pb-1 hover:gap-3 transition-all">
            See full inventory <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
